import { createSlice } from '@reduxjs/toolkit';
import type {
  Department,
  CreateUserResponse,
  UserInfo,
  UserInfoForEdit,
} from '@/types/authresponse';
import type { PaginatedUserList, User } from '@/types/userlist';
import type { ReduxBaseStateInterface } from '@/types/component';
import { userPermissions } from '@/constants/auth-constants';

export interface UserStateInterface {
  // authUserData: Data of signed in user
  authUserData: ReduxBaseStateInterface<UserInfo> & {
    userPermissions: typeof userPermissions;
  };
  // userData: Data for particular use like update user
  userData: ReduxBaseStateInterface<UserInfoForEdit>;
  users: ReduxBaseStateInterface<User[]>;
  userList: ReduxBaseStateInterface<PaginatedUserList>;
  departments: ReduxBaseStateInterface<Department[]>;
  postAWSSignUp: ReduxBaseStateInterface<CreateUserResponse>;
  postAddUser: ReduxBaseStateInterface<null>;
  uploadProfile: ReduxBaseStateInterface<null>;
}

const initialState: UserStateInterface = {
  authUserData: {
    loading: false,
    error: false,
    message: '',
    cached: false,
    data: {} as UserInfo,
    userPermissions: structuredClone(userPermissions),
  },
  userList: {
    loading: false,
    error: false,
    message: '',
    data: {} as PaginatedUserList,
    cached: false,
  },
  users: {
    loading: false,
    error: false,
    message: '',
    data: [],
    cached: false,
  },
  departments: {
    loading: false,
    error: false,
    message: '',
    data: [],
    cached: false,
  },
  userData: {
    loading: false,
    error: false,
    message: '',
    data: {} as UserInfoForEdit,
    cached: false,
  },
  postAWSSignUp: {
    loading: false,
    error: false,
    message: '',
    data: {} as CreateUserResponse,
    cached: false,
  },
  postAddUser: {
    loading: false,
    error: false,
    message: '',
    data: null,
    cached: false,
  },
  uploadProfile: {
    loading: false,
    error: false,
    message: '',
    data: null,
    cached: false,
  },
};

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    startLoading: (state, action) => {
      const type: keyof UserStateInterface = action.payload;
      state[type].loading = true;
      state[type].cached = false;
    },
    stopLoading: (state, action) => {
      const type: keyof UserStateInterface = action.payload.type;
      const error = action.payload?.error || true;
      state[type].loading = false;

      if (error) {
        state[type].cached = true;
        state[type].data = initialState[type].data;
      }
    },
    saveFetchedData: (state, action) => {
      const type: keyof UserStateInterface = action.payload.type;
      state[type].loading = false;
      state[type].data = action.payload.data;
      state[type].error = false;
      state[type].cached = true;
    },
    dataFetchedWithError: (state, action) => {
      const type: keyof UserStateInterface = action.payload.type;
      state[type].data = initialState[type].data;
      state[type].loading = false;
      state[type].error = true;
      state[type].cached = true;
      state[type].message = action.payload.message;
    },
    clearData: (state, action) => {
      const type: keyof UserStateInterface = action.payload;
      state[type].data = initialState[type].data;
      state[type].cached = false;
      state[type].error = false;
      state[type].message = '';
      state[type].loading = false;
    },
    saveAuthUserData: (state, action) => {
      const data = action.payload as UserInfo;
      const _state = state.authUserData;
      _state.loading = false;
      _state.data = data;
      _state.error = false;
      _state.cached = true;
      _state.userPermissions = structuredClone(userPermissions); // reset before adding permissions
      data.permissions.forEach((permission) => {
        _state.userPermissions[permission.tag] = true;
      });
    },
    reset() {
      return structuredClone(initialState);
    },
    resetKeepAuth: (state) => {
      return {
        ...structuredClone(initialState),
        authUserData: state.authUserData,
      };
    },
  },
});

export const userActions = userSlice.actions;

export const userReducer = userSlice.reducer;
